'use strict';

// ⚠ .cjs 는 webpack 이 babel 없이 직접 파싱한다 → `??` / `?.` 금지 (production 빌드가 깨진다). 기존 .cjs 헬퍼들과 같은 제약.
//
// 프로젝트 단위 결과보고서 일괄 실행(생성 → 확정 → ZIP)의 분기 로직 전부.
// Vue / DOM / jsPDF / axios 를 일절 참조하지 않는다 — 리포트 mixin 은 node 에서 require 할 수 없으므로
// (jspdf·vuex import, apps/supply/vue.config.js:70-82 에만 있는 @shared_mixins/* alias, PNG 바이너리)
// 이 파일이 node --test 로 검증 가능한 유일한 표면이다. I/O 는 전부 호출자가 주입한다.

// 페이지 하한은 "업로드할 때만" 건다.
//
// ZIP 전용 실행(확정 꺼짐)은 서버에 아무것도 쓰지 않는다. 결과물이 이상하면 운영자가 보고 지우고 다시 돌리면
// 그만이라 자가 교정된다 — 거기서 반려하는 건 값을 못 하고 정상 보고서를 막을 위험만 있다.
// 반대로 업로드하면 그 PDF 가 확정을 거쳐 원청 포털의 공식 산출물이 된다. 넥센 백지 사고(d44c570)가
// 정확히 이 경로였다: 렌더러가 예외를 삼키고 2페이지 부분 문서를 반환 → 검증 없이 업로드·확정.
//
// 4페이지인 근거: generatePDF_supply 는 표지·요약·코드요약·코드결과를 무조건 그린다(:387-394).
// 즉 구조적 최소치라 정상 보고서를 반려할 수 없다. staging SEAH_S_26 실측 25건은 9~47페이지였다.
//
// 바이트 하한은 뺐다. 같은 실측에서 페이지가 5배 늘어도 용량은 1.4배뿐이었다(9p=4.89MB, 47p=6.86MB) —
// 용량 대부분이 내용이 아니라 임베드 폰트·배경 이미지라 완성도를 판정하지 못한다.
// 하던 일이 "렌더러가 돌기는 했나" 뿐인데 그건 페이지 수가 더 정확히 한다. 중복이었다.
const DEFAULT_MIN_PAGES = 4;

// 백엔드 오류 본문은 평문·JSON 이 섞여 있다. data.message 만 보면 평문 사유가 버려지고 'Request failed with status code 404' 만 남는다.
const {getErrorMessage} = require('../../../../core/utils/api/errorMessage.js');

function seatMode (row) {
    return row.service_type === '일반' ? 'DOC' : 'OSIS';
}

function errText (e) {
    if (!e) return '알 수 없는 오류';
    return getErrorMessage(e, '') || e.message || String(e);
}

// 평가가 끝나지 않아 점수가 없는 업체인지. /report/data 의 EV_COUNT 레시피 결과(results[].name === 'category3')에서
// 코드가 하나도 없거나, 등급(category3_ev_code)이 빈 코드가 있으면 그렇다. 빈 등급은 그 코드의 문항 판정이 하나도 없다는 뜻이고
// (백엔드 EvCode.aggr), 렌더러는 그 코드를 '해당없음'처럼 그린다 — 고객에게 낼 수 없는 보고서다.
// 레시피 결과가 없는 다른 보고서 유형은 판정할 수 없으므로 막지 않는다.
function scoreMissing (reportData) {
    const results = reportData && reportData.results;
    const cate3 = Array.isArray(results) ? results.find(r => r && r.name === 'category3') : null;
    if (!cate3 || !Array.isArray(cate3.data)) return false;
    return !cate3.data.length || cate3.data.some(r => r.category3_ev_code == null);
}

function statusOf (e) {
    if (e && e.response && e.response.status) return e.response.status;
    if (e && e.status) return e.status;
    return null;
}

// ZIP 안에서 파일명이 겹치면 뒤엣것이 앞엣것을 덮어쓴다 (fflate zipSync 는 평범한 객체 키).
// 서로 다른 두 업체가 같은 보고서명을 가질 수 있어 company_id 를 붙여 구분한다.
// ReportBulkSubmitter.vue:1400-1425 의 usedFilenames 처리와 같은 규칙.
function uniqueName (used, baseName, companyId) {
    let name = baseName + '.pdf';
    if (used.has(name)) {
        name = baseName + '_' + companyId + '.pdf';
    }
    let n = 2;
    while (used.has(name)) {
        name = baseName + '_' + companyId + '_' + n + '.pdf';
        n += 1;
    }
    used.add(name);
    return name;
}

// GET /report (업체×캠페인 좌석 행) + GET /report/history (확정 이력) → 실행 대상 행.
//
// 레거시(ReportBulkGenerator.vue:494-536) 와 다른 점 3가지. 전부 실제 버그다:
//   1. 이력을 company_id 로만 키잉했다(:497). 확정은 mode(DOC/OSIS) 별로 따로 존재하므로
//      DOC 확정 id 가 OSIS 행의 확정 id 로 새어 들어간다 — 그 id 가 DELETE /report/confirm 에 그대로 쓰인다.
//   2. 이력 정렬이 없어 응답 순서상 마지막 행이 이겼다. 이제 time_stamp 내림차순 첫 행.
//   3. 행 모양은 첫 좌석(:503)에서, mode 는 마지막 좌석(:527)에서 뽑아 서로 어긋났다. 이제 같은 좌석에서 뽑는다.
//
// 업체 중복 제거는 레거시와 동일하게 "응답 순서상 첫 좌석" 이다. 서버 정렬을 신뢰하며,
// 근거 없는 재정렬을 넣지 않는다.
function buildRunPlan (rosterRows, historyRows) {
    const confirmedByKey = {};
    const hasConfirmedDoc = {};

    (historyRows || []).forEach(h => {
        if (h.deleted_at) return;
        if (!h.confirmed) return;

        const key = h.company_id + ':' + h.mode;
        const prev = confirmedByKey[key];
        if (!prev || String(h.time_stamp) > String(prev.time_stamp)) {
            confirmedByKey[key] = h;
        }
        if (h.mode === 'DOC') hasConfirmedDoc[h.company_id] = true;
    });

    const seen = new Set();
    const rows = [];

    (rosterRows || []).forEach(r => {
        if (seen.has(r.company_id)) return;
        seen.add(r.company_id);

        const mode = seatMode(r);
        const confirmed = confirmedByKey[r.company_id + ':' + mode];

        rows.push({
            project_code: r.project_code,
            campaign_id: r.campaign_id,
            company_id: r.company_id,
            company_name: r.company_name,
            request_id: r.request_id,
            status_code: r.status_code,
            service_type: r.service_type,
            period: r.period,
            mode: mode,
            confirmed_history_id: confirmed ? confirmed.id : null,
            already_confirmed: !!confirmed,
            // ManagingResultReport_v2.vue:1065-1068 — 실사평가는 같은 업체의 서면평가 확정이 선행돼야 한다.
            // 클라이언트 전용 규칙이라 서버 강제 여부는 미확인. 그래서 "확정" 만 막고 생성·ZIP 은 허용한다
            // (전체를 막으면 확정 없이 산출물만 받는 복구 경로가 같이 막힌다).
            confirm_blocked: mode === 'OSIS' && !hasConfirmedDoc[r.company_id],
            // 이미 확정된 업체는 기본 해제 — 재실행이 멀쩡한 확정을 갈아엎지 않게 한다. 다시 체크하면 갱신된다.
            selected: !confirmed,
        });
    });

    return rows;
}

// 레거시(ReportBulkGenerator.vue:768-781) 는 DELETE(기존 확정 해제) → PUT(신규 확정) 순서라
// PUT 이 실패하면 업체가 "확정 0건" 으로 남는다 — 시작 전보다 나쁜 상태다.
// 그래서 PUT 을 먼저 시도한다. 서버가 409(기존 확정 존재, ManagingResultReport_v2.vue:1092)를 주면
// 그때만 기존을 해제하고 다시 확정하고, 그 재확정마저 실패하면 기존 확정을 되살린다.
async function confirmWithRestore (io, row, newHistoryId) {
    try {
        await io.confirm(newHistoryId);
        return;
    } catch (e) {
        if (statusOf(e) !== 409 || !row.confirmed_history_id) throw e;
    }

    await io.unconfirm(row.confirmed_history_id);

    try {
        await io.confirm(newHistoryId);
    } catch (e) {
        try {
            await io.confirm(row.confirmed_history_id);
        } catch (restoreError) {
            e.restoreFailed = true;
        }
        throw e;
    }
}

// 업체 목록을 순차 처리한다. 동시 실행하지 않는다 — 백엔드가 동시 요청을 견디는지에 대한 근거가 레포에 없다.
//
// io 주입 (전부 호출자 = .vue 가 제공):
//   io.fetchData(row)                   → reportData (조회만 — 건너뛸 업체는 렌더러를 띄우지 않는다)
//   io.prepare(reportData, row)         → (선택) 렌더러 준비. 점수 게이트를 통과한 업체만 부른다
//   io.filename(reportData, row)        → 확장자 없는 파일명
//   io.render(reportData, filename, row)→ jsPDF doc (render_warnings / render_decor_warnings 가 붙어 올 수 있다)
//   io.toBlob(doc, filename)            → Blob (그대로 ZIP 으로 넘어간다)
//   io.upload(row, blob, filename)      → report_history_id
//   io.confirm(reportId)                → 성공 또는 throw (409 는 statusOf 로 판별)
//   io.unconfirm(reportId)
// opt:
//   opt.confirm     확정 단계 수행 여부 (false 면 서버 쓰기 0건 — 생성·ZIP 만)
//   opt.cancelled() 업체 시작 전마다 확인. true 면 즉시 중단 (진행 중 업체는 건드리지 않는다)
//   opt.onProgress(index, row, phase)
//   opt.minPages   업로드 시 요구하는 최소 페이지 수 (ZIP 전용 실행에는 적용되지 않는다)
async function runBulk (targets, io, opt) {
    const options = opt || {};
    const minPages = options.minPages || DEFAULT_MIN_PAGES;
    const onProgress = options.onProgress || function () {};

    const done = [];
    const failed = [];
    // 업로드·확정은 됐지만 로고 같은 장식이 빠진 업체 (막지 않고 결과에만 보인다)
    const warned = [];
    const files = [];
    const usedNames = new Set();
    let cancelled = false;

    for (let i = 0; i < targets.length; i++) {
        const row = targets[i];

        if (options.cancelled && options.cancelled()) {
            cancelled = true;
            break;
        }

        let phase = 'data';
        let releasedHistoryId = null;
        const willUpload = !!(options.confirm && !row.confirm_blocked);

        try {
            onProgress(i, row, phase);
            const reportData = await io.fetchData(row);

            // 점수 없는 업체는 그려 봐야 올릴 수 없다. 업로드할 때만 렌더 전에 건너뛴다 (ZIP 전용은 위 페이지 하한과 같은 이유로 그대로).
            if (willUpload && scoreMissing(reportData)) {
                phase = 'skip';
                throw new Error('점수 없음(평가 미완료)');
            }

            phase = 'render';
            onProgress(i, row, phase);
            if (io.prepare) await io.prepare(reportData, row);
            const filename = io.filename(reportData, row);
            const doc = await io.render(reportData, filename, row);

            phase = 'validate';
            // 널 체크는 임계값이 아니라 방어다. BulkReportContainer.vue:95-107 의 catch 가 undefined 를
            // 반환하므로, 없으면 한참 뒤 toBlob 에서 "Cannot read properties of undefined" 로 터진다.
            if (!doc || typeof doc.getNumberOfPages !== 'function') {
                throw new Error('렌더러가 문서를 반환하지 않았습니다.');
            }

            // 페이지 하한은 업로드할 때만. ZIP 전용은 서버에 안 쓰고 운영자가 눈으로 걸러 자가 교정된다.
            if (willUpload) {
                const pages = doc.getNumberOfPages();
                if (pages < minPages) {
                    throw new Error('생성된 보고서가 ' + pages + '페이지뿐입니다 (최소 ' + minPages + '). 업로드하지 않았습니다.');
                }
            }

            const blob = io.toBlob(doc, filename);
            files.push({name: uniqueName(usedNames, filename, row.company_id), blob: blob});

            if (willUpload) {
                // 렌더러는 그리다 실패한 부분을 예외 없이 빼고 문서에 이름만 붙인다(report.core.mixin.js addRenderWarning).
                // 고객이 받는 내용이 빠졌으면 페이지 하한처럼 업로드만 막는다. 파일은 위에서 이미 ZIP 에 넣었다.
                if (doc.render_warnings && doc.render_warnings.length) {
                    throw new Error('빠진 부분: ' + doc.render_warnings.join(', ') + '. 업로드하지 않았습니다.');
                }

                phase = 'upload';
                onProgress(i, row, phase);
                releasedHistoryId = await io.upload(row, blob, filename);
                if (!releasedHistoryId) {
                    throw new Error('업로드 응답에 report_history_id 가 없습니다.');
                }

                phase = 'confirm';
                onProgress(i, row, phase);
                await confirmWithRestore(io, row, releasedHistoryId);
            }

            done.push(row.company_id);
            if (willUpload && doc.render_decor_warnings && doc.render_decor_warnings.length) {
                warned.push({
                    company_id: row.company_id,
                    company_name: row.company_name,
                    reason: '빠진 부분: ' + doc.render_decor_warnings.join(', '),
                });
            }
        } catch (e) {
            failed.push({
                company_id: row.company_id,
                company_name: row.company_name,
                phase: phase,
                reason: errText(e),
                // upload 이후에 죽었으면 서버에 이력 행이 남는다. 숨기지 말고 번호를 알려준다.
                released_history_id: releasedHistoryId,
                restore_failed: !!e.restoreFailed,
            });
        }
    }

    return {done: done, failed: failed, warned: warned, files: files, cancelled: cancelled};
}

module.exports = {
    buildRunPlan: buildRunPlan,
    runBulk: runBulk,
    scoreMissing: scoreMissing,
    uniqueName: uniqueName,
    seatMode: seatMode,
};
